import type { CSSProperties, ElementType, ReactNode } from 'react';

/**
 * Literal max-width classes. Kept as a static map (not an interpolated
 * `max-w-${width}`) so Tailwind's static extractor keeps them in the build.
 */
const WIDTHS: Record<string, string> = {
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
};

interface SectionShellProps {
  children: ReactNode;
  /** Extra classes appended after the standard container (e.g. `pt-12 pb-8`, `space-y-4`). */
  className?: string;
  /** Render element — defaults to `div`. Use `section`/`aside` for semantic blocks. */
  as?: ElementType;
  /** Content max-width. Defaults to `6xl` (the standard lens-page column). */
  width?: keyof typeof WIDTHS;
  /** Add the standard top divider (`border-t border-border-subtle`) between stacked sections. */
  divider?: boolean;
  /** Inline styles (e.g. safe-area insets) passed through to the container element. */
  style?: CSSProperties;
  /** Accessible label when rendered as a landmark (`section`/`aside`/`nav`). */
  'aria-label'?: string;
}

/**
 * Canonical page-section container: one horizontal rhythm (`mx-auto max-w-6xl
 * px-4 md:px-8`) shared by every stacked block on a lens page. Centralising the
 * container here kills the width/padding drift the design-craft review flagged
 * (peer blocks each re-declaring the same `max-w-6xl px-4 md:px-8` string, then
 * diverging over time). Per-use vertical spacing is passed via `className`; the
 * common between-section divider is opted into with `divider`.
 *
 * Refs: almanak-57rz.12 (primitive), almanak-97ks (app-wide adoption)
 */
export function SectionShell({
  children,
  className = '',
  as: Tag = 'div',
  width = '6xl',
  divider = false,
  style,
  'aria-label': ariaLabel,
}: SectionShellProps) {
  const cls = [
    'mx-auto',
    WIDTHS[width],
    'px-4',
    'md:px-8',
    divider && 'border-t border-border-subtle',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <Tag className={cls} style={style} aria-label={ariaLabel}>
      {children}
    </Tag>
  );
}
